import { useState } from 'react';

import { NewQueryModal } from '@/sections/insights/queryBuilder/components/Modals';
import { QueryFolderContent } from '@/sections/insights/queryBuilder/components/QueryBuilderSidebar/QueryFolderContent';
import { QueryFolderHeader } from '@/sections/insights/queryBuilder/components/QueryBuilderSidebar/QueryFolderHeader';
import { useUnsavedChangesPrompt } from '@/sections/insights/queryBuilder/components/QueryBuilderSidebar/useUnsavedChangesPrompt';
import type {
  QueryFolderData,
  QueryFolderType,
} from '@/sections/insights/queryBuilder/savedQueries/folders';
import { QueryFolders } from '@/sections/insights/queryBuilder/savedQueries/folders';
import { useQueryBuilder } from '@/sections/insights/queryBuilder/state/useQueryBuilder';
import { SavedQuery } from '@/sections/insights/queryBuilder/types/SavedQuery';

interface QueryFolderProps {
  folderData: QueryFolderData;
  queries: SavedQuery[];
  isExpanded: boolean;
  onFolderClick: (folderId: QueryFolderType) => void;
}

/**
 * QueryFolder - Individual folder in the Query Builder sidebar
 *
 * Renders a collapsible folder with:
 * - Folder header with icon, label, count, and chevron
 * - Expandable content showing list of queries
 * - Empty state for folders with no queries
 */
export const QueryFolder = ({
  folderData: folderData,
  isExpanded,
  queries,
  onFolderClick,
}: QueryFolderProps) => {
  const { queryState, setQuery, setMode } = useQueryBuilder();
  const [isNewQueryModalOpen, setIsNewQueryModalOpen] = useState(false);
  const { confirmIfDirty, modal } = useUnsavedChangesPrompt();

  const selectedQueryId = queryState?.metadata.id ?? null;
  const isCustomQueriesFolder = folderData.id === QueryFolders.CUSTOM_QUERIES;

  const handleQueryClick = (queryId: string) => {
    const query = queries.find(q => q.id === queryId);
    if (!query || query.id === selectedQueryId) return;

    confirmIfDirty(() => {
      setQuery(query);
      setMode('query');
    });
  };

  const handleNewQueryClick = () => {
    setIsNewQueryModalOpen(true);
  };

  return (
    <div data-testid={`folder-${folderData.id}`}>
      <QueryFolderHeader
        folderData={folderData}
        queryCount={queries.length}
        isExpanded={isExpanded}
        onFolderClick={onFolderClick}
      />

      <QueryFolderContent
        queries={queries}
        isExpanded={isExpanded}
        folderId={folderData.id}
        isCustomQueriesFolder={isCustomQueriesFolder}
        selectedQueryId={selectedQueryId}
        onQueryClick={handleQueryClick}
        onNewQueryClick={handleNewQueryClick}
      />

      {/* New Query Modal */}
      {isCustomQueriesFolder && (
        <NewQueryModal
          key={isNewQueryModalOpen.toString()} // forces modal to remount on open/close
          isOpen={isNewQueryModalOpen}
          onClose={() => setIsNewQueryModalOpen(false)}
        />
      )}

      {modal}
    </div>
  );
};
